
import React, { useEffect, useState } from "react";

const BANNER_API =
  "https://api.vanyacollectionsrajahmundry.com/banner";

const BannerManagement = () => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [description, setDescription] = useState("");

  const [bannerExists, setBannerExists] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // =====================================================
  // LOAD BANNER
  // GET /banner/all
  // =====================================================

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      setLoading(true);
      setMessage("");

      const apiUrl =
        `${BANNER_API}/all`;

      console.log(
        "GET BANNER API:",
        apiUrl
      );

      const response =
        await fetch(apiUrl);

      // =================================================
      // NO BANNER
      // =================================================

      if (response.status === 404) {
        setBannerExists(false);
        setImagePreview("");
        setDescription("");
        setImageFile(null);

        return;
      }

      // =================================================
      // API ERROR
      // =================================================

      if (!response.ok) {
        throw new Error(
          "Failed to fetch banner"
        );
      }

      // =================================================
      // RESPONSE
      // =================================================

      const data =
        await response.json();

      console.log(
        "GET BANNER RESPONSE:",
        data
      );

      // =================================================
      // SUPPORT DIFFERENT RESPONSE FORMATS
      // =================================================

      let banner;

      if (Array.isArray(data)) {
        banner = data[0];
      } else {
        banner =
          data.banner || data;
      }

      // =================================================
      // BANNER FOUND
      // =================================================

      if (
        banner &&
        (
          banner.image_url ||
          banner.description
        )
      ) {
        setBannerExists(true);

        setImagePreview(
          banner.image_url || ""
        );

        setDescription(
          banner.description || ""
        );
      }

      // =================================================
      // NO BANNER
      // =================================================

      else {
        setBannerExists(false);

        setImagePreview("");
        setDescription("");
      }

      setImageFile(null);

    } catch (error) {
      console.error(
        "Fetch banner error:",
        error
      );

      setMessage(
        error.message ||
          "Failed to load banner"
      );

      setMessageType("error");

    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // SELECT IMAGE
  // NO TYPE LIMIT
  // NO SIZE LIMIT
  // =====================================================

  const handleImageChange = (e) => {
    const file =
      e.target.files?.[0];

    if (!file) {
      return;
    }

    // =================================================
    // NO IMAGE TYPE VALIDATION
    // NO IMAGE SIZE VALIDATION
    // =================================================

    setImageFile(file);

    // =================================================
    // CREATE PREVIEW
    // =================================================

    const previewUrl =
      URL.createObjectURL(file);

    setImagePreview(previewUrl);

    setMessage("");
    setMessageType("");
  };

  // =====================================================
  // REMOVE SELECTED NEW IMAGE
  // =====================================================

  const handleRemoveImage = async () => {
    setImageFile(null);

    await fetchBanner();
  };

  // =====================================================
  // ADD BANNER
  // POST /banner/add
  // =====================================================

  const handleAddBanner = async () => {
    try {
      setSaving(true);
      setMessage("");
      setMessageType("");

      // =================================================
      // DESCRIPTION VALIDATION
      // =================================================

      if (!description.trim()) {
        setMessage(
          "Please enter a banner description."
        );

        setMessageType("error");
        setSaving(false);

        return;
      }

      // =================================================
      // IMAGE REQUIRED
      // =================================================

      if (!imageFile) {
        setMessage(
          "Please select a banner image."
        );

        setMessageType("error");
        setSaving(false);

        return;
      }

      // =================================================
      // FORM DATA
      // =================================================

      const formData =
        new FormData();

      formData.append(
        "image",
        imageFile
      );

      formData.append(
        "description",
        description.trim()
      );

      setMessage(
        "Uploading banner image and adding..."
      );

      setMessageType("info");

      // =================================================
      // ADD API
      // =================================================

      const addUrl =
        `${BANNER_API}/add`;

      console.log(
        "ADD BANNER API:",
        addUrl
      );

      const response =
        await fetch(
          addUrl,
          {
            method: "POST",
            body: formData,
          }
        );

      const data =
        await response.json();

      console.log(
        "ADD BANNER RESPONSE:",
        data
      );

      // =================================================
      // ERROR
      // =================================================

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Failed to add banner"
        );
      }

      // =================================================
      // SUCCESS
      // =================================================

      setMessage(
        "Banner added successfully!"
      );

      setMessageType("success");

      setImageFile(null);

      setBannerExists(true);

      // Reload banner
      await fetchBanner();

    } catch (error) {
      console.error(
        "Add banner error:",
        error
      );

      setMessage(
        error.message ||
          "Failed to add banner"
      );

      setMessageType("error");

    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // UPDATE BANNER
  // PUT /banner/update
  // =====================================================

  const handleUpdateBanner = async () => {
    try {
      setSaving(true);
      setMessage("");
      setMessageType("");

      // =================================================
      // DESCRIPTION VALIDATION
      // =================================================

      if (!description.trim()) {
        setMessage(
          "Please enter a banner description."
        );

        setMessageType("error");
        setSaving(false);

        return;
      }

      // =================================================
      // FORM DATA
      // =================================================

      const formData =
        new FormData();

      // Image optional during update
      if (imageFile) {
        formData.append(
          "image",
          imageFile
        );
      }

      formData.append(
        "description",
        description.trim()
      );

      // =================================================
      // MESSAGE
      // =================================================

      if (imageFile) {
        setMessage(
          "Uploading new banner image and updating..."
        );
      } else {
        setMessage(
          "Updating banner description..."
        );
      }

      setMessageType("info");

      // =================================================
      // UPDATE API
      // =================================================

      const updateUrl =
        `${BANNER_API}/update`;

      console.log(
        "UPDATE BANNER API:",
        updateUrl
      );

      const response =
        await fetch(
          updateUrl,
          {
            method: "PUT",
            body: formData,
          }
        );

      const data =
        await response.json();

      console.log(
        "UPDATE BANNER RESPONSE:",
        data
      );

      // =================================================
      // ERROR
      // =================================================

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Failed to update banner"
        );
      }

      // =================================================
      // SUCCESS
      // =================================================

      setMessage(
        "Banner updated successfully!"
      );

      setMessageType("success");

      setImageFile(null);

      setBannerExists(true);

      // Reload banner
      await fetchBanner();

    } catch (error) {
      console.error(
        "Update banner error:",
        error
      );

      setMessage(
        error.message ||
          "Failed to update banner"
      );

      setMessageType("error");

    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // ADD OR UPDATE
  // =====================================================

  const handleSaveBanner = () => {
    if (bannerExists) {
      handleUpdateBanner();
    } else {
      handleAddBanner();
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loader}></div>

        <p>
          Loading Banner...
        </p>
      </div>
    );
  }

  // =====================================================
  // BUTTON TEXT
  // =====================================================

  const buttonText = saving
    ? bannerExists
      ? "UPDATING..."
      : "ADDING..."
    : bannerExists
    ? "UPDATE BANNER"
    : "ADD BANNER";

  // =====================================================
  // UI
  // =====================================================

  return (
    <div style={styles.container}>

      {/* =================================================
          HEADER
      ================================================= */}

      <div style={styles.header}>

        <div>
          <h1 style={styles.title}>
            Hero Banner
          </h1>

          <p style={styles.subtitle}>
            Manage your homepage hero banner
          </p>
        </div>

        {/* STATUS */}

        <div
          style={{
            ...styles.statusBadge,

            ...(bannerExists
              ? styles.activeBadge
              : styles.emptyBadge),
          }}
        >
          {bannerExists
            ? "● Banner Active"
            : "○ No Banner Added"}
        </div>

      </div>

      {/* =================================================
          MESSAGE
      ================================================= */}

      {message && (
        <div
          style={{
            ...styles.message,

            ...(messageType === "success"
              ? styles.successMessage
              : messageType === "error"
              ? styles.errorMessage
              : styles.infoMessage),
          }}
        >
          {message}
        </div>
      )}

      {/* =================================================
          MAIN CARD
      ================================================= */}

      <div className="banner-card-responsive" style={styles.card}>

        {/* =================================================
            BANNER IMAGE
        ================================================= */}

        <div style={styles.section}>

          <label style={styles.label}>
            Banner Image
          </label>

          <p style={styles.helpText}>
            Select the image that will appear
            on your homepage hero section.
          </p>

          {/* =================================================
              UPLOAD BOX
          ================================================= */}

          <label
            style={{
              ...styles.uploadBox,

              ...(saving
                ? styles.uploadBoxDisabled
                : {}),
            }}
          >

            <input
              type="file"
              onChange={handleImageChange}
              style={styles.hiddenInput}
              disabled={saving}
            />

            <div style={styles.uploadIcon}>
              📷
            </div>

            <div style={styles.uploadTitle}>
              Click to select banner image
            </div>

            <div style={styles.uploadText}>
              Select any image
            </div>

          </label>

          {/* =================================================
              BANNER PREVIEW
          ================================================= */}

          {imagePreview && (
            <div style={styles.previewWrapper}>

              <div style={styles.previewHeader}>

                <span style={styles.previewTitle}>
                  Current Banner
                </span>

                {imageFile && (
                  <span
                    style={
                      styles.newImageBadge
                    }
                  >
                    New Image
                  </span>
                )}

              </div>

              <div
                style={
                  styles.previewContainer
                }
              >

                <img
                  src={imagePreview}
                  alt="Hero Banner"
                  style={styles.preview}
                />

              </div>

              {/* =================================================
                  REMOVE SELECTED IMAGE
              ================================================= */}

              {imageFile && (
                <button
                  type="button"
                  onClick={
                    handleRemoveImage
                  }
                  style={
                    styles.removeButton
                  }
                  disabled={saving}
                >
                  ✕ Remove Selected Image
                </button>
              )}

            </div>
          )}

        </div>

        {/* =================================================
            DESCRIPTION
        ================================================= */}

        <div style={styles.section}>

          <label style={styles.label}>
            Hero Description
          </label>

          <p style={styles.helpText}>
            This text will appear below the
            main hero heading.
          </p>

          <textarea
            value={description}
            onChange={(e) =>
              setDescription(
                e.target.value
              )
            }
            placeholder="Enter hero section description..."
            rows={6}
            style={styles.textarea}
            disabled={saving}
          />

          <div style={styles.counter}>
            {description.length} characters
          </div>

        </div>

        {/* =================================================
            ADD / UPDATE BUTTON
        ================================================= */}

        <button
          type="button"
          onClick={handleSaveBanner}
          disabled={saving}
          style={{
            ...styles.button,

            ...(saving
              ? styles.buttonDisabled
              : {}),
          }}
        >
          {buttonText}
        </button>

      </div>

      <style>{`
        @media (max-width: 768px) {
          .banner-card-responsive {
            padding: 18px 14px !important;
            border-radius: 14px !important;
          }
        }
      `}</style>
    </div>
  );
};

// =====================================================
// STYLES
// =====================================================

const styles = {
  container: {
    padding: "10px 0 30px",
    maxWidth: "1000px",
    margin: "0 auto",
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  },

  header: {
    marginBottom: "25px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "20px",
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    color: "#14352b",
    fontSize: "26px",
    fontWeight: "800",
  },

  subtitle: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  statusBadge: {
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
  },

  activeBadge: {
    background: "#ecfdf5",
    color: "#065f46",
    border: "1px solid #a7f3d0",
  },

  emptyBadge: {
    background: "#fffbeb",
    color: "#92400e",
    border: "1px solid #fde68a",
  },

  card: {
    background: "#ffffff",
    padding: "30px",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
  },

  section: {
    marginBottom: "30px",
  },

  label: {
    display: "block",
    fontWeight: "700",
    color: "#14352b",
    marginBottom: "4px",
    fontSize: "15px",
  },

  helpText: {
    color: "#64748b",
    fontSize: "13px",
    marginTop: "0",
    marginBottom: "12px",
  },

  hiddenInput: {
    display: "none",
  },

  uploadBox: {
    border: "2px dashed #cbd5e1",
    borderRadius: "12px",
    padding: "32px 20px",
    textAlign: "center",
    cursor: "pointer",
    display: "block",
    background: "#f8fafc",
    transition: "border-color 0.2s ease",
  },

  uploadBoxDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  uploadIcon: {
    fontSize: "32px",
    marginBottom: "8px",
  },

  uploadTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#14352b",
  },

  uploadText: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px",
  },

  previewWrapper: {
    marginTop: "20px",
  },

  previewHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
  },

  previewTitle: {
    fontWeight: "700",
    color: "#14352b",
    fontSize: "14px",
  },

  newImageBadge: {
    background: "#fdf8ea",
    color: "#b89326",
    border: "1px solid #d4af37",
    padding: "3px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "700",
  },

  previewContainer: {
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    background: "#f1f5f9",
  },

  preview: {
    width: "100%",
    maxHeight: "450px",
    objectFit: "cover",
    display: "block",
  },

  removeButton: {
    marginTop: "10px",
    background: "#ffffff",
    color: "#dc2626",
    border: "1px solid #fecaca",
    padding: "7px 14px",
    borderRadius: "7px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
  },

  textarea: {
    width: "100%",
    padding: "14px",
    border: "1.5px solid #cbd5e1",
    borderRadius: "10px",
    resize: "vertical",
    fontSize: "14px",
    lineHeight: "1.6",
    fontFamily: "inherit",
    outline: "none",
    color: "#0f172a",
    boxSizing: "border-box",
  },

  counter: {
    textAlign: "right",
    marginTop: "5px",
    color: "#94a3b8",
    fontSize: "12px",
  },

  button: {
    background: "linear-gradient(135deg, #14352b 0%, #1f4a3c 100%)",
    color: "#D4AF37",
    border: "none",
    padding: "13px 28px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "800",
    letterSpacing: "0.5px",
    fontSize: "14px",
    minWidth: "180px",
    boxShadow: "0 4px 12px rgba(20, 53, 43, 0.25)",
  },

  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  message: {
    padding: "12px 16px",
    borderRadius: "10px",
    marginBottom: "20px",
    maxWidth: "1000px",
    fontSize: "14px",
    fontWeight: "600",
  },

  successMessage: {
    background: "#ecfdf5",
    color: "#065f46",
    border: "1px solid #a7f3d0",
  },

  errorMessage: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
  },

  infoMessage: {
    background: "#eff6ff",
    color: "#1e40af",
    border: "1px solid #bfdbfe",
  },

  loading: {
    padding: "60px",
    textAlign: "center",
    color: "#14352b",
  },

  loader: {
    width: "35px",
    height: "35px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #14352b",
    borderRadius: "50%",
    margin: "0 auto 15px",
    animation: "spin 1s linear infinite",
  },
};

export default BannerManagement;
